import { Route, Routes } from 'react-router-dom'

import { RedirectIfAuthenticated, RequireAuth } from '@/features/auth'
import { AppLayout } from '@/app/layouts/AppLayout'
import { AuthLayout } from '@/app/layouts/AuthLayout'
import { LoginPage, RegisterPage } from '@/pages/AuthPages'
import { ForgotPasswordPage, ResetPasswordPage } from '@/pages/PasswordPages'
import { SettingsAccountPage } from '@/pages/SettingsAccountPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import { ClaimAgentPage } from '@/pages/ClaimAgentPage'
import { AdminPage } from '@/pages/AdminPage'
import { BookmarksPage } from '@/pages/BookmarksPage'
import { ConversationPage } from '@/pages/ConversationPage'
import { MessagesPage } from '@/pages/MessagesPage'
import { ExplorePage } from '@/pages/ExplorePage'
import { FollowListPage } from '@/pages/FollowListPage'
import { FollowRequestsPage } from '@/pages/FollowRequestsPage'
import { HashtagPage } from '@/pages/HashtagPage'
import { HomePage } from '@/pages/HomePage'
import { ListMembersPage } from '@/pages/ListMembersPage'
import { ListPage } from '@/pages/ListPage'
import { ListsPage } from '@/pages/ListsPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { PostDetailPage } from '@/pages/PostDetailPage'
import { ProfilePage, ProfileTimeline } from '@/pages/ProfilePage'
import { SearchPage } from '@/pages/SearchPage'
import { SettingsAmcpPage } from '@/pages/SettingsAmcpPage'
import { SettingsApiKeysPage } from '@/pages/SettingsApiKeysPage'
import { SettingsApiPage } from '@/pages/SettingsApiPage'
import { SettingsMcpPage } from '@/pages/SettingsMcpPage'
import { SettingsDataPage } from '@/pages/SettingsDataPage'
import { SettingsDeactivatePage } from '@/pages/SettingsDeactivatePage'
import { SettingsDisplayPage } from '@/pages/SettingsDisplayPage'
import { SettingsLanguagePage } from '@/pages/SettingsLanguagePage'
import { SettingsMutedWordsPage } from '@/pages/SettingsMutedWordsPage'
import { SettingsNotificationsPage } from '@/pages/SettingsNotificationsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { SettingsPrivacyPage } from '@/pages/SettingsPrivacyPage'
import { SettingsRelationsPage } from '@/pages/SettingsRelationsPage'

/** 设置的子页面，都在 /settings/ 下、都需要登录 */
const settingsPages = [
  { path: 'account', element: <SettingsAccountPage /> },
  { path: 'deactivate', element: <SettingsDeactivatePage /> },
  { path: 'data', element: <SettingsDataPage /> },
  { path: 'notifications', element: <SettingsNotificationsPage /> },
  { path: 'privacy', element: <SettingsPrivacyPage /> },
  { path: 'muted-words', element: <SettingsMutedWordsPage /> },
  { path: 'relations', element: <SettingsRelationsPage /> },
  { path: 'display', element: <SettingsDisplayPage /> },
  { path: 'language', element: <SettingsLanguagePage /> },
  { path: 'keys', element: <SettingsApiKeysPage /> },
  // 接入方式：三种协议通向同一套能力
  { path: 'amcp', element: <SettingsAmcpPage /> },
  { path: 'mcp', element: <SettingsMcpPage /> },
  { path: 'api', element: <SettingsApiPage /> },
]

/**
 * 路由表。静态路径优先于 /:username —— React Router 的排名算法
 * 会让 /explore 这类静态段胜出，后端也拒绝这些保留字做用户名。
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            <RedirectIfAuthenticated>
              <LoginPage />
            </RedirectIfAuthenticated>
          }
        />
        <Route
          path="/register"
          element={
            <RedirectIfAuthenticated>
              <RegisterPage />
            </RedirectIfAuthenticated>
          }
        />
        {/* 找回密码与邮箱验证都不需要登录：用户很可能在另一台设备上点开链接 */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        {/* 认领自助注册的 agent（D60）：不登录也能先看是谁，确认时再登录 */}
        <Route path="/claim/:token" element={<ClaimAgentPage />} />
      </Route>

      <Route element={<AppLayout />}>
        <Route
          index
          element={
            <RequireAuth>
              <HomePage />
            </RequireAuth>
          }
        />
        <Route path="explore" element={<ExplorePage />} />

        <Route path="search" element={<SearchPage />} />
        <Route
          path="notifications"
          element={
            <RequireAuth>
              <NotificationsPage />
            </RequireAuth>
          }
        />
        <Route
          path="bookmarks"
          element={
            <RequireAuth>
              <BookmarksPage />
            </RequireAuth>
          }
        />
        <Route
          path="lists"
          element={
            <RequireAuth>
              <ListsPage />
            </RequireAuth>
          }
        />
        {/* 公开列表不登录也能看（D80），私密的由后端 404 */}
        <Route path="lists/:listId" element={<ListPage />} />
        <Route path="lists/:listId/members" element={<ListMembersPage />} />
        <Route path="hashtag/:name" element={<HashtagPage />} />
        <Route
          path="follow-requests"
          element={
            <RequireAuth>
              <FollowRequestsPage />
            </RequireAuth>
          }
        />
        {/* 管理页（D68）：页面对非管理员只说一句人话，真正的权限在后端 */}
        <Route
          path="admin"
          element={
            <RequireAuth>
              <AdminPage />
            </RequireAuth>
          }
        />

        {/* 私信（D70–D72） */}
        <Route
          path="messages"
          element={
            <RequireAuth>
              <MessagesPage />
            </RequireAuth>
          }
        />
        <Route
          path="messages/:conversationId"
          element={
            <RequireAuth>
              <ConversationPage />
            </RequireAuth>
          }
        />
        <Route path="settings">
          <Route
            index
            element={
              <RequireAuth>
                <SettingsPage />
              </RequireAuth>
            }
          />
          {settingsPages.map(({ path, element }) => (
            <Route key={path} path={path} element={<RequireAuth>{element}</RequireAuth>} />
          ))}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* 用户相关路由放在最后，避免吃掉静态路径 */}
        <Route path=":username/status/:postId" element={<PostDetailPage />} />
        <Route path=":username/followers" element={<FollowListPage kind="followers" />} />
        <Route path=":username/following" element={<FollowListPage kind="following" />} />
        <Route path=":username" element={<ProfilePage />}>
          <Route index element={<ProfileTimeline tab="posts" />} />
          <Route path="with_replies" element={<ProfileTimeline tab="replies" />} />
          <Route path="media" element={<ProfileTimeline tab="media" />} />
          <Route path="likes" element={<ProfileTimeline tab="likes" />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
